/* eslint-disable no-nested-ternary */
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import H1 from '@base/H1/H1';
import P1 from '@base/P1/P1';
import Divider from '@base/Divider/Divider';
import Button from '@base/Button/Button';
import { getDay, getMonthString, getYear } from '@utils/dates';
import Image from '@base/Image';
import useWindowSize from '@hooks/useWindowSize';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { emptyObject, emptyList } from '@constants/empty';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import { subscriptionTypes } from '@constants/index';
import { getSubscriptionTypeLabelFromType } from '@utils/subscriptionHelpers/getSubscriptionTypeForAnalytics';
import { segment } from '@actions/analytics';
import {
  getPlanTagDimensions,
  getPlanTypeTagImage
} from '../bundleAddOnsHelper';
import { CHECKOUT_OFFER_CLAIMED } from '../constants/segment';

const liteTagIcon = 'bundles/liteTagSmall.png';

const getSubscriptionIconByType = (subType, isMobile = false) => {
  if (subType === subscriptionTypes.LITE) return liteTagIcon;
  return getPlanTypeTagImage(subType, isMobile);
};
const Flex = styled.div`
  display: flex;
`;

const FlexCenter = styled(Flex)`
  align-items: center;
`;

const OfferCardCont = styled.div`
  position: relative;
  padding: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_24B};
  height: 100%;
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  margin: 0 ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin: 0;
  }
`;

const OfferCardFooter = styled.div`
  position: absolute;
  padding: ${Spacings.SPACING_4B};
  left: 0;
  bottom: 0;
  height: 72px;
  width: 100%;
  border-top: 1px dashed var(--color-divider);
`;

const Cut = styled.div`
  position: absolute;
  width: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
  bottom: 64px;
  background-color: var(--color-base-0);
  border-radius: 50%;
`;

const LeftCut = styled(Cut)`
  left: -8px;
`;

const RightCut = styled(Cut)`
  right: -8px;
`;

const ClaimBtn = styled(Button)`
  margin-left: auto;
`;

const DurNumLabel = styled(H1)`
  font-size: 52px !important;
  line-height: 40px !important;
  margin-right: ${Spacings.SPACING_2B} !important;
`;

const DurUnitLabel = styled(H1)`
  font-size: 26px !important;
  line-height: 18px !important;
  margin-bottom: ${Spacings.SPACING_1B} !important;
`;

const DurUnitSubLabel = styled(H1)`
  font-size: 16px !important;
  line-height: 18px !important;
`;

const GoalCont = styled.div`
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_3B};
  margin-top: ${Spacings.SPACING_4B};
`;

const GoalRow = styled(FlexCenter)`
  margin-top: 10px;
  &:first-child {
    margin-top: 0;
  }
`;

const IconTag = styled.div`
  flex-shrink: 0;
  display: contents;
`;

const GoalNameLabel = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  margin: 0 ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_2B};
  line-height: 28px;
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.P2};
  }
`;

const StyledDivider = styled(Divider)`
  margin-top: 10px;
  height: 1px;
  background-color: var(--color-divider);
`;

const OfferBenefitLabel = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  line-height: 20px;
`;

const DurSubCont = styled.div`
  margin-top: 1px;
`;

const GoalDetails = ({ subType, goalName }) => {
  const { isTablet } = useWindowSize();
  return (
    <GoalRow>
      <IconTag>
        <Image
          width={getPlanTagDimensions(isTablet).width}
          height={getPlanTagDimensions(isTablet).height}
          src={getSubscriptionIconByType(subType, isTablet)}
          alt="type"
        />
      </IconTag>
      <GoalNameLabel color="var(--color-text-primary)">
        {goalName}
      </GoalNameLabel>
    </GoalRow>
  );
};

GoalDetails.propTypes = {
  subType: PropTypes.number.isRequired,
  goalName: PropTypes.string.isRequired
};

const getSubscriptionType = (subType) => {
  const label = getSubscriptionTypeLabelFromType(subType);
  return label === 'NA' ? 'Combo' : label;
};

const OfferCard = ({ offer }) => {
  const { push } = useRouter();
  const subType = offer.subscription_type;
  const isCombo = subType === subscriptionTypes.COMBO;
  const offerValidTill = offer.valid_till;
  const offerValidTillTxt =
    offerValidTill &&
    `Offer expires ${getDay(offerValidTill)} ${getMonthString(
      offerValidTill
    )} ${getYear(offerValidTill)}`;
  const [offeredDuration, offeredDurationMeasure] =
    offer.offered_duration.split(' ');
  const goalDetails = offer.goal_details || emptyObject;
  const comboDetails = goalDetails.add_on_goal || emptyObject;
  const primaryDetails = goalDetails.primary_goal || emptyObject;
  const addOnTopologies = comboDetails.topologies || emptyList;
  const subscriptionDetails = offer.subscription_details || emptyObject;

  const claimHandler = () => {
    segment.track(CHECKOUT_OFFER_CLAIMED, {
      goal_name: primaryDetails.name,
      goal_uid: primaryDetails.uid,
      is_combo: isCombo,
      offer_uid: offer.uid,
      offered_duration: offer.offered_duration_days,
      allowed_duration: offer.allowed_duration_days,
      subscription_type: getSubscriptionType(subType)
    });
    if (isCombo) {
      push({
        pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
        query: {
          goalSlug: comboDetails.slug,
          goalUID: comboDetails.uid,
          subscriptionUID: subscriptionDetails.add_on_uid,
          gUid: primaryDetails.uid
        }
      });
    } else {
      push({
        pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
        query: {
          goalSlug: isCombo ? comboDetails.slug : primaryDetails.slug,
          goalUID: isCombo ? comboDetails.uid : primaryDetails.uid,
          subscriptionUID: subscriptionDetails.primary_uid,
          gUid: isCombo && primaryDetails.uid
        }
      });
    }
  };

  const renderGoals = () => {
    return (
      <>
        {isCombo ? (
          <>
            {addOnTopologies.map((goal, index) => {
              return (
                <>
                  <GoalDetails subType={goal.type} goalName={goal.name} />
                  {index < addOnTopologies.length - 1 && <StyledDivider />}
                </>
              );
            })}
          </>
        ) : (
          <GoalDetails subType={subType} goalName={primaryDetails.name} />
        )}
      </>
    );
  };

  return (
    <OfferCardCont>
      <Flex>
        <DurNumLabel as="span" color="var(--color-i-orange-2)">
          {offeredDuration}
        </DurNumLabel>
        <DurSubCont>
          <DurUnitLabel as="span" color="var(--color-i-orange-2)">
            {offeredDurationMeasure}
          </DurUnitLabel>
          <DurUnitSubLabel as="span" color="var(--color-i-orange-2)">
            extra
          </DurUnitSubLabel>
        </DurSubCont>
      </Flex>
      <OfferBenefitLabel color="var(--color-text-primary)">
        {offer.offer_benefit_text}
      </OfferBenefitLabel>
      <GoalCont>{renderGoals()}</GoalCont>
      <OfferCardFooter>
        <FlexCenter>
          <P1 color="var(--color-text-secondary)">{offerValidTillTxt}</P1>
          <ClaimBtn
            label="Claim now"
            onClick={claimHandler}
            grid={2}
            width="102px"
          />
        </FlexCenter>
      </OfferCardFooter>
      <LeftCut />
      <RightCut />
    </OfferCardCont>
  );
};

OfferCard.propTypes = {
  offer: PropTypes.objectOf(PropTypes.any).isRequired
};

export default OfferCard;
